import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCheckCircleLight, PiWarningLight, PiXCircleLight, PiBellLight } from 'react-icons/pi';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface AlertProps {
  type: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  message: string;
  subMessage?: string;
  onClose?: () => void;
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  }>;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ type, message, subMessage, onClose, actions }, ref) => {
    const { t } = useTranslation();
    const accentColors = {
      success: 'bg-success-50 dark:bg-success-500/10 text-success-500 dark:text-success-400',
      warning: 'bg-warning-50 dark:bg-warning-500/10 text-warning-500 dark:text-warning-400',
      error: 'bg-danger-50 dark:bg-danger-500/10 text-danger-500 dark:text-danger-400',
      info: 'bg-info-50 dark:bg-info-500/10 text-info-500 dark:text-info-400',
      neutral: 'bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400',
    };

    const iconComponents = {
      success: <PiCheckCircleLight className="w-4 h-4" />,
      warning: <PiWarningLight className="w-4 h-4" />,
      error: <PiXCircleLight className="w-4 h-4" />,
      info: <InformationCircleIcon className="w-4 h-4" />,
      neutral: <PiBellLight className="w-4 h-4" />,
    };

    const borderColors = {
      success: 'border-success-200/60 dark:border-success-800/60',
      warning: 'border-warning-200/60 dark:border-warning-800/60',
      error: 'border-danger-200/60 dark:border-danger-800/60',
      info: 'border-info-200/60 dark:border-info-800/60',
      neutral: 'border-gray-200/60 dark:border-gray-700/60',
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'alert',
          'bg-white/95 dark:bg-gray-800/95 backdrop-blur-md',
          'text-gray-800 dark:text-gray-200',
          'border',
          borderColors[type],
          'rounded-xl px-4 py-3 shadow-lg shadow-black/5 dark:shadow-black/20',
          'min-w-[320px] max-w-md'
        )}
        role="alert"
      >
        <div className={clsx('flex gap-3', subMessage || (actions && actions.length > 0) ? 'items-start' : 'items-center')}>
          <div className={clsx('flex-shrink-0', (subMessage || (actions && actions.length > 0)) && 'mt-0.5')}>
            <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', accentColors[type])}>
              {iconComponents[type]}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100 leading-tight">{message}</p>
            {subMessage && <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 leading-snug">{subMessage}</p>}
            {actions && actions.length > 0 && (
              <div className="flex items-center gap-2 mt-2.5">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.onClick}
                    className={clsx(
                      'px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors duration-150',
                      {
                        'bg-primary-500 text-white hover:bg-primary-600': action.variant === 'primary' || !action.variant,
                        'bg-secondary-500 text-white hover:bg-secondary-600': action.variant === 'secondary',
                        'bg-success-500 text-white hover:bg-success-600': action.variant === 'success',
                        'bg-warning-500 text-white hover:bg-warning-600': action.variant === 'warning',
                        'bg-danger-500 text-white hover:bg-danger-600': action.variant === 'error',
                      }
                    )}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className={clsx('flex-shrink-0 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-150', (subMessage || (actions && actions.length > 0)) && 'mt-0.5')}
              aria-label="Close"
            >
              <span className="sr-only">{t('Close')}</span>
              <PiXCircleLight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';

export default Alert;